import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { API, getUserInfo } from "@/api";
import type { UserInfo } from "@/api/type";
import { canManageSiteScopedCommerce } from "@/common/permissions";
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Modal, ModalBody, Row, Table } from "@/Components/ui";
import { dialogService } from "@/utils/dialogService";

type OptionProductRow = {
    id: number;
    image?: string;
    name: string;
    price: number | string;
    type: string;
    tax_free: boolean;
};

const TAX_FREE_LABEL = {
    true: "면세",
    false: "면세 아님",
};

export default function OptionProductList() {
    const navigate = useNavigate();
    const [data, setData] = useState<OptionProductRow[]>([]);
    const [search, setSearch] = useState("");
    const [delRecord, setDelRecord] = useState<number | null>(null);
    const [modalDelete, setModalDelete] = useState(false);
    const [currentUser, setCurrentUser] = useState<UserInfo>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const canManageCommerce = useMemo(() => canManageSiteScopedCommerce(currentUser), [currentUser]);

    const guardOwnerAction = useCallback(async (action: () => void) => {
        if (!canManageCommerce) {
            await dialogService.handleAlert("지점 소유자 또는 최상위 관리자만 작업할 수 있습니다.");
            return;
        }
        action();
    }, [canManageCommerce]);

    const fetchOptions = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await API.get("/package/options/");
            const responseData = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.results)
                    ? res.data.results
                    : [];
            setData(responseData);
        }
        catch (error) {
            console.error(error);
            await dialogService.handleAlert("옵션 상품 목록을 불러오지 못했습니다.", { force: "error" });
            setData([]);
        }
        finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        getUserInfo()
            .then((info) => setCurrentUser(info))
            .catch(() => setCurrentUser(null));
        fetchOptions();
    }, [fetchOptions]);

    const filteredData = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) {
            return data;
        }

        return data.filter((item) => {
            const searchTarget = [
                String(item.id),
                item.name ?? "",
                String(item.price ?? ""),
                item.type ?? "",
                TAX_FREE_LABEL[String(Boolean(item.tax_free)) as "true" | "false"],
            ].join(" ").toLowerCase();
            return searchTarget.includes(keyword);
        });
    }, [data, search]);

    const closeDeleteModal = useCallback(() => {
        setModalDelete(false);
        setDelRecord(null);
    }, []);

    const handleCreate = useCallback(() => {
        guardOwnerAction(() => {
            navigate("/option-add-product/0");
        });
    }, [guardOwnerAction, navigate]);

    const handleEdit = useCallback((id: number) => {
        guardOwnerAction(() => {
            navigate(`/option-add-product/${id}`);
        });
    }, [guardOwnerAction, navigate]);

    const handleDelete = useCallback((id: number) => {
        guardOwnerAction(() => {
            setDelRecord(id);
            setModalDelete(true);
        });
    }, [guardOwnerAction]);

    const deleteOption = useCallback(async () => {
        if (!delRecord) {
            return;
        }

        setIsDeleting(true);
        try {
            await API.delete(`/package/options/${delRecord}/`);
            closeDeleteModal();
            await fetchOptions();
            await dialogService.handleAlert("Delete ok!");
        }
        catch (error: any) {
            await dialogService.handleAlert(error?.message ?? "Delete failed", { force: "error" });
        }
        finally {
            setIsDeleting(false);
        }
    }, [closeDeleteModal, delRecord, fetchOptions]);

    return (<React.Fragment>
      <style>{`
        .option-product-row > th,
        .option-product-row > td {
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
        }
      `}</style>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="옵션상품" pageTitle="옵션"/>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                  <h4 className="card-title mb-0">옵션 상품 목록</h4>
                  <div>
                    <Button color="success" className="add-btn me-1" onClick={handleCreate} id="create-btn">
                      <i className="ri-add-line align-bottom me-1"></i> 생성
                    </Button>
                  </div>
                </CardHeader>

                <CardBody>
                  <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
                    <div className="w-full max-w-md">
                      <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="상품명, 타입, 가격으로 검색"/>
                    </div>
                    <div className="text-muted d-flex align-items-center">
                      {isLoading ? "불러오는 중..." : `총 ${filteredData.length}개`}
                    </div>
                  </div>

                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead className="table-light">
                        <tr className="option-product-row">
                          <th>ID</th>
                          <th>이미지</th>
                          <th>상품명</th>
                          <th>가격</th>
                          <th>타입</th>
                          <th>면세여부</th>
                          <th className="text-end">관리</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length === 0 ? (<tr>
                            <td colSpan={7} className="text-center text-muted py-5">
                              {isLoading ? "옵션 상품을 불러오는 중입니다." : "표시할 옵션 상품이 없습니다."}
                            </td>
                          </tr>) : (filteredData.map((item) => (<tr key={item.id} className="option-product-row">
                              <td>{item.id}</td>
                              <td>
                                {item.image ? (<img src={item.image} alt={`${item.name} 썸네일`} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}/>) : (<span className="text-muted">이미지 없음</span>)}
                              </td>
                              <td className="fw-semibold">{item.name}</td>
                              <td>{item.price}</td>
                              <td>{item.type}</td>
                              <td>{TAX_FREE_LABEL[String(Boolean(item.tax_free)) as "true" | "false"]}</td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button color="success" size="sm" className="edit-item-btn" onClick={() => handleEdit(item.id)}>
                                    수정
                                  </Button>
                                  <Button color="danger" size="sm" className="remove-item-btn" onClick={() => handleDelete(item.id)}>
                                    삭제
                                  </Button>
                                </div>
                              </td>
                            </tr>)))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalDelete} onClose={closeDeleteModal} centered variant="dialog">
        <ModalBody className="text-center p-5">
          <div className="mt-4">
            <h4 className="mb-3">Are you Sure ?</h4>
            <p className="text-muted mb-4">{`Are you Sure You want to Remove this Record(id:${delRecord}) ?`}</p>
            <div className="hstack gap-2 justify-content-center">
              <Button color="light" onClick={closeDeleteModal} disabled={isDeleting}>
                Cancel
              </Button>
              <Button color="danger" onClick={deleteOption} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>);
}
